/**
 * LogoUpload Component
 *
 * Upload company logo to Supabase Storage OR use external URL
 * Handles file validation, preview, and upload
 * Uses modal for clear user experience
 */

import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import InfoTooltip from './InfoTooltip';

export default function LogoUpload({ currentLogoUrl, contractorId, onUploadSuccess }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentLogoUrl);
  const [error, setError] = useState(null);
  const [urlInput, setUrlInput] = useState(currentLogoUrl || '');
  const [showModal, setShowModal] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null); // 'upload' or 'url'

  // Update URL input when currentLogoUrl changes (when contractor data loads)
  useEffect(() => {
    if (currentLogoUrl) {
      setPreview(currentLogoUrl);
      setUrlInput(currentLogoUrl);
    }
  }, [currentLogoUrl]);

  const handleFileSelect = async (event) => {
    try {
      setError(null);
      const file = event.target.files[0];

      if (!file) return;

      // Validation
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        setError('File size must be less than 5MB');
        return;
      }

      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        setError('File must be PNG or JPG format');
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);

      // Upload to Supabase Storage
      setUploading(true);

      const fileExt = file.name.split('.').pop();
      const fileName = `${contractorId}-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      console.log('Uploading logo to Supabase Storage:', filePath);

      const { data, error: uploadError } = await supabase.storage
        .from('company-logos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw uploadError;
      }

      console.log('Upload successful:', data);

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('company-logos')
        .getPublicUrl(filePath);

      console.log('Public URL generated:', publicUrl);

      // Callback with new URL (triggers auto-save in Settings)
      onUploadSuccess(publicUrl);

      // Reset state and close modal
      setSelectedMethod(null);
      setShowModal(false);
      setError(null);

    } catch (err) {
      console.error('Logo upload error:', err);
      setError(err.message || 'Failed to upload logo. Please try again.');
    } finally {
      setUploading(false);
      // Reset file input so user can upload the same file again if needed
      event.target.value = '';
    }
  };

  const handleUrlSubmit = () => {
    setError(null);

    if (!urlInput.trim()) {
      setError('Please enter a URL');
      return;
    }

    // Basic URL validation
    try {
      new URL(urlInput);
      setPreview(urlInput);
      console.log('Using logo URL:', urlInput);

      // Callback with URL (triggers auto-save in Settings)
      onUploadSuccess(urlInput);

      // Reset state and close modal
      setSelectedMethod(null);
      setShowModal(false);
      setError(null);
    } catch (err) {
      setError('Please enter a valid URL (e.g., https://example.com/logo.png)');
    }
  };

  return (
    <>
      <div>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
          Company Logo
          <InfoTooltip text="This logo will be displayed on assessment reports shared with your customers." />
        </label>

        {/* Preview */}
        {preview ? (
          <div style={{
            marginBottom: '12px',
            padding: '12px',
            background: '#f8fafc',
            borderRadius: '8px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '80px',
            position: 'relative'
          }}>
            <img
              src={preview}
              alt="Company Logo"
              style={{
                maxWidth: '120px',
                maxHeight: '80px',
                objectFit: 'contain'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                setError('Failed to load image from URL');
              }}
            />
          </div>
        ) : (
          <div style={{
            marginBottom: '12px',
            padding: '20px 15px',
            background: '#f8fafc',
            borderRadius: '8px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '80px',
            border: '2px dashed #cbd5e1'
          }}>
            <div style={{ textAlign: 'center', color: '#94a3b8' }}>
              <div style={{ fontSize: '32px', marginBottom: '4px' }}>🖼️</div>
              <div style={{ fontSize: '13px' }}>No logo set</div>
            </div>
          </div>
        )}

        {/* Add/Change Logo Button */}
        <button
          type="button"
          onClick={() => {
            setShowModal(true);
            setSelectedMethod(null);
            setError(null);
          }}
          style={{
            padding: '12px 24px',
            background: '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            width: '100%'
          }}
        >
          {preview ? 'Change Logo' : 'Add Logo'}
        </button>
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
          padding: '20px'
        }} onClick={() => {
          setShowModal(false);
          setSelectedMethod(null);
          setError(null);
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }} onClick={(e) => e.stopPropagation()}>

            {/* Step 1: Choose Method */}
            {!selectedMethod && (
              <>
                <h2 style={{ fontSize: '24px', marginBottom: '10px', color: '#1e293b' }}>
                  {preview ? 'Change' : 'Add'} Company Logo
                </h2>
                <p style={{ color: '#64748b', marginBottom: '30px', fontSize: '14px' }}>
                  Choose how you'd like to add your logo
                </p>

                {/* Option 1: Upload File */}
                <button
                  type="button"
                  onClick={() => setSelectedMethod('upload')}
                  style={{
                    width: '100%',
                    padding: '20px',
                    background: '#f8fafc',
                    borderRadius: '8px',
                    marginBottom: '15px',
                    border: '2px solid #e2e8f0',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.background = '#f0f4ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.background = '#f8fafc';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div style={{ fontSize: '32px' }}>📤</div>
                    <div>
                      <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px', color: '#1e293b' }}>
                        Upload from Computer
                      </h3>
                      <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                        PNG or JPG, max 5MB, recommended 500x500px
                      </p>
                    </div>
                  </div>
                </button>

                {/* Option 2: Use URL */}
                <button
                  type="button"
                  onClick={() => setSelectedMethod('url')}
                  style={{
                    width: '100%',
                    padding: '20px',
                    background: '#f8fafc',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    border: '2px solid #e2e8f0',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.background = '#f0f4ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.background = '#f8fafc';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div style={{ fontSize: '32px' }}>🔗</div>
                    <div>
                      <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px', color: '#1e293b' }}>
                        Use Image URL
                      </h3>
                      <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                        Link to an image hosted online
                      </p>
                    </div>
                  </div>
                </button>

                {/* Cancel Button */}
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setError(null);
                  }}
                  style={{
                    padding: '12px',
                    background: '#f1f5f9',
                    color: '#64748b',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    width: '100%'
                  }}
                >
                  Cancel
                </button>
              </>
            )}

            {/* Step 2A: Upload File */}
            {selectedMethod === 'upload' && (
              <>
                <button
                  type="button"
                  onClick={() => setSelectedMethod(null)}
                  style={{
                    padding: '8px 12px',
                    background: 'transparent',
                    border: 'none',
                    color: '#667eea',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    marginBottom: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  ← Back
                </button>

                <h2 style={{ fontSize: '24px', marginBottom: '10px', color: '#1e293b' }}>
                  Upload Logo
                </h2>
                <p style={{ color: '#64748b', marginBottom: '30px', fontSize: '14px' }}>
                  Select an image file from your computer
                </p>

                <div style={{
                  padding: '40px 20px',
                  background: '#f8fafc',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  border: '2px dashed #cbd5e1',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '15px' }}>📤</div>
                  <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '6px' }}>
                    PNG or JPG, max 5MB
                  </div>
                  <div style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '20px' }}>
                    Recommended: 500x500px square
                  </div>

                  <label
                    htmlFor="logo-upload-modal"
                    style={{
                      display: 'inline-block',
                      padding: '12px 24px',
                      background: uploading ? '#94a3b8' : '#667eea',
                      color: 'white',
                      borderRadius: '8px',
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      transition: 'all 0.2s'
                    }}
                  >
                    {uploading ? 'Uploading...' : 'Choose File'}
                  </label>
                  <input
                    id="logo-upload-modal"
                    type="file"
                    accept="image/png,image/jpeg,image/jpg"
                    onChange={handleFileSelect}
                    disabled={uploading}
                    style={{ display: 'none' }}
                  />
                </div>

                {error && (
                  <div style={{
                    padding: '12px',
                    background: '#fee2e2',
                    border: '2px solid #ef4444',
                    borderRadius: '8px',
                    color: '#dc2626',
                    fontSize: '14px',
                    marginBottom: '15px'
                  }}>
                    <strong>Error:</strong> {error}
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setSelectedMethod(null);
                    setError(null);
                  }}
                  style={{
                    padding: '12px',
                    background: '#f1f5f9',
                    color: '#64748b',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    width: '100%'
                  }}
                >
                  Cancel
                </button>
              </>
            )}

            {/* Step 2B: Use URL */}
            {selectedMethod === 'url' && (
              <>
                <button
                  type="button"
                  onClick={() => setSelectedMethod(null)}
                  style={{
                    padding: '8px 12px',
                    background: 'transparent',
                    border: 'none',
                    color: '#667eea',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    marginBottom: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  ← Back
                </button>

                <h2 style={{ fontSize: '24px', marginBottom: '10px', color: '#1e293b' }}>
                  Use Image URL
                </h2>
                <p style={{ color: '#64748b', marginBottom: '30px', fontSize: '14px' }}>
                  Enter the URL of an image hosted online
                </p>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#1e293b' }}>
                    Image URL
                  </label>
                  <input
                    type="url"
                    className="input"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    placeholder="https://example.com/logo.png"
                    style={{ fontSize: '14px', marginBottom: '12px' }}
                    autoFocus
                  />
                  <div style={{ fontSize: '13px', color: '#64748b' }}>
                    💡 Tip: Make sure the URL is publicly accessible
                  </div>
                </div>

                {error && (
                  <div style={{
                    padding: '12px',
                    background: '#fee2e2',
                    border: '2px solid #ef4444',
                    borderRadius: '8px',
                    color: '#dc2626',
                    fontSize: '14px',
                    marginBottom: '15px'
                  }}>
                    <strong>Error:</strong> {error}
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleUrlSubmit}
                  style={{
                    padding: '12px',
                    background: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    width: '100%',
                    marginBottom: '10px'
                  }}
                >
                  Use This URL
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setSelectedMethod(null);
                    setError(null);
                  }}
                  style={{
                    padding: '12px',
                    background: '#f1f5f9',
                    color: '#64748b',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    width: '100%'
                  }}
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
